import { Button, Drawer, IconButton } from "@heathmont/moon-core-tw";
import {
  ControlsCloseSmall,
  GenericAlarmRound,
} from "@heathmont/moon-icons-tw";
import CalendarSwap from "./CalendarSwap";
import { ShiftDayDetails } from "..";
import { useState } from "react";
import {
  getEligibleSwaps,
  proposeSwap,
} from "../../../services/request.service";
import moment from "moment";

interface SwapsProps {
  shifts: ShiftDayDetails[];
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  month: number;
  year: number;
}

interface SelectedDate {
  month: number;
  day: number;
  year: number;
  shift: "day" | "night";
}

interface EligibleColleague {
  colleague: { _id: string; firstName: string; lastName: string; role: string };
  colleagueShift: { day: number; month: number; year: number; shiftType: string };
}

const SwapDrawer = ({ isOpen, setIsOpen, shifts, month, year }: SwapsProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedDate, setSelectedDate] = useState<SelectedDate | null>(null);
  const [eligible, setEligible] = useState<EligibleColleague[]>([]);
  const [selected, setSelected] = useState<EligibleColleague | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setStep(1);
    setSelectedDate(null);
    setEligible([]);
    setSelected(null);
    setError("");
    setSuccess(false);
  };

  const handleDiscard = () => {
    reset();
    setIsOpen(false);
  };

  const handleNext = async () => {
    if (!selectedDate) return;
    setError("");
    setLoading(true);
    const res: any = await getEligibleSwaps(
      selectedDate.day,
      selectedDate.month + 1,
      selectedDate.year,
      selectedDate.shift
    );
    setLoading(false);
    if (res?.res) {
      setEligible(Array.isArray(res.res.data) ? res.res.data : []);
      setStep(2);
    } else {
      setError("Could not load eligible colleagues.");
    }
  };

  const handleApply = async () => {
    if (!selected || !selectedDate) return;
    setSubmitting(true);
    setError("");
    const res: any = await proposeSwap({
      colleagueId: selected.colleague._id,
      requesterShift: {
        day: selectedDate.day,
        month: selectedDate.month + 1,
        year: selectedDate.year,
        shiftType: selectedDate.shift,
      },
      colleagueShift: selected.colleagueShift,
    });
    setSubmitting(false);
    if (res?.res) {
      setSuccess(true);
      setTimeout(() => handleDiscard(), 1800);
    } else {
      setError(res?.err?.message || "Failed to send swap request.");
    }
  };

  const formatShift = (s: EligibleColleague) =>
    `${moment({ year: s.colleagueShift.year, month: s.colleagueShift.month - 1, day: s.colleagueShift.day }).format("DD MMM")} · ${s.colleagueShift.shiftType}`;

  return (
    <Drawer open={isOpen} setOpen={setIsOpen}>
      <Drawer.Panel className="max-w-[391px] flex flex-col">
        <div className="flex justify-between items-center p-3 border-b border-beerus">
          <h3 className="text-moon-20 font-bold transition-colors">
            {step === 1 ? "Select Your Shift" : "Choose Colleague"}
          </h3>
          <IconButton variant="ghost" onClick={handleDiscard}>
            <ControlsCloseSmall />
          </IconButton>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {step === 1 && (
            <>
              <p className="text-moon-14 text-trunks mb-4">
                Pick the shift you want to swap away.
              </p>
              <CalendarSwap
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                shifts={shifts}
                shift="day"
                month={month}
                year={year}
              />
              <div className="mt-4 space-y-2">
                <div className="flex items-center">
                  <span className="w-4 h-4 mr-3 bg-[#ffca4f] rounded-full"></span>
                  <span className="text-gray-700">Day Shift</span>
                </div>
                <div className="flex items-center">
                  <span className="w-4 h-4 mr-3 bg-[#272d36] rounded-full"></span>
                  <span className="text-gray-700">Night Shift</span>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {selectedDate && (
                <div className="mb-4 p-3 bg-slate-50 rounded-[8px]">
                  <p className="text-moon-12 text-trunks">Your shift to give away</p>
                  <p className="text-moon-14 font-semibold capitalize">
                    {selectedDate.shift} shift ·{" "}
                    {moment({ year: selectedDate.year, month: selectedDate.month, day: selectedDate.day }).format("DD MMM YYYY")}
                  </p>
                </div>
              )}

              <h4 className="text-moon-14 font-semibold mb-2">Swap with</h4>

              {loading && (
                <p className="text-moon-14 text-trunks">Finding eligible colleagues…</p>
              )}

              {!loading && eligible.length === 0 && !error && (
                <p className="text-moon-14 text-trunks">
                  No eligible colleagues found for this shift.
                </p>
              )}

              {!loading && eligible.length > 0 && (
                <div className="space-y-2 max-h-[260px] overflow-y-auto">
                  {eligible.map((item) => (
                    <button
                      key={item.colleague._id + item.colleagueShift.day}
                      onClick={() => setSelected(item)}
                      className={`w-full text-left rounded-[8px] border p-3 transition-colors ${
                        selected?.colleague._id === item.colleague._id
                          ? "border-hit bg-hit/10"
                          : "border-beerus hover:border-hit/40"
                      }`}
                    >
                      <p className="text-moon-14 font-semibold">
                        {item.colleague.firstName} {item.colleague.lastName}
                        <span className="text-trunks font-normal ml-1 text-moon-12 capitalize">
                          · {item.colleague.role}
                        </span>
                      </p>
                      <p className="text-moon-12 text-trunks mt-0.5">
                        You'd take their:{" "}
                        <span className="font-semibold text-bulma">{formatShift(item)}</span>
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {success && (
                <p className="mt-3 text-moon-13 text-green-600 font-semibold">
                  Swap request sent to {selected?.colleague.firstName}!
                </p>
              )}
            </>
          )}

          {error && (
            <p className="mt-2 text-moon-13 text-red-500">{error}</p>
          )}
        </div>

        <div className="px-[24px]">
          <div className="flex items-start space-x-2">
            <i className="text-moon-18 leading-1">
              <GenericAlarmRound />
            </i>
            <p className="text-moon-14 text-gray-600 flex-1">
              <span className="font-semibold"> Note: </span>
              {step === 1
                ? "Select one of your scheduled shifts from the calendar."
                : "The colleague must accept before the swap takes effect."}
            </p>
          </div>
        </div>

        <hr className="border-t border-beerus mt-[24px]" />

        <div className="p-6 flex items-center space-x-3">
          {step === 1 ? (
            <>
              <Button onClick={reset} variant="ghost" disabled={!selectedDate}>
                Reset
              </Button>
              <div className="flex items-center flex-1 justify-end space-x-3">
                <Button onClick={handleDiscard} variant="secondary">
                  Discard
                </Button>
                <Button
                  onClick={handleNext}
                  className="bg-hit"
                  disabled={!selectedDate || loading}
                >
                  {loading ? "Loading…" : "Next"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button onClick={() => { setStep(1); setSelected(null); setError(""); }} variant="ghost">
                Back
              </Button>
              <div className="flex items-center flex-1 justify-end space-x-3">
                <Button onClick={handleDiscard} variant="secondary">
                  Discard
                </Button>
                <Button
                  onClick={handleApply}
                  className="bg-hit"
                  disabled={!selected || submitting || success}
                >
                  {submitting ? "Sending…" : "Send Request"}
                </Button>
              </div>
            </>
          )}
        </div>
      </Drawer.Panel>
      <Drawer.Backdrop />
    </Drawer>
  );
};

export default SwapDrawer;
